import * as z from "zod";

export type ReviewOutcome = "approved" | "comment" | "changes_requested";

export type ReviewSeverity = "P1" | "P2" | "P3";
export type ReviewFindingStatus = "fixed" | "still_open" | "invalid";
export type ReviewRisk = {
  level: "low" | "medium" | "high";
  reasons: string[];
};

export interface ReviewFileComment {
  path: string;
  line: number;
  body: string;
  /** Absent on reports recorded before reviews rated severity. */
  severity?: ReviewSeverity;
  /** Absent on reports recorded before CI coverage was classified. */
  ci_covered?: boolean;
  /** Review agents that reported this finding; absent on older reports. */
  agents?: string[];
  /** The diff lines ending at `line`, cut at the reviewed head, e.g. `"@@ -4,2 +4,2 @@\n a\n+b"`. */
  excerpt?: string;
}

export interface ReviewPriorFinding {
  id: string;
  head_sha: string;
  path: string;
  line: number;
  body: string;
  severity: ReviewSeverity;
  /** Absent on findings tracked before CI coverage was classified. */
  ci_covered?: boolean;
  status: ReviewFindingStatus;
  reason: string;
}

export interface ReviewResult {
  outcome: ReviewOutcome;
  summary_comment: string;
  file_comments: ReviewFileComment[];
  prior_findings: ReviewPriorFinding[];
  risk?: ReviewRisk;
}

export type ReviewVerdictDecision = "approve" | "changes_needed";

const REVIEW_APPROVAL_STATES = [
  "approved",
  "approving",
  "would_approve",
  "approval_refused",
  "approval_unconfirmed",
  "withdrawn",
  "would_withdraw",
  "covered_by_earlier_approval",
  "changes_needed",
  "auto_approve_off",
  "p1_declined",
  "proof_failed",
  "risk_not_assessed",
  "approved_but_risky",
  "awaiting_proof",
  "draft",
  "closed",
  "fork",
  "bot_author",
  "head_moved",
  "base_changed",
  "merge_base_changed",
  "reviewed_base_missing",
  "findings_not_posted",
  "github_unavailable",
  "no_lead",
] as const;
export type ReviewApprovalState = (typeof REVIEW_APPROVAL_STATES)[number];
const ReviewFindingDisposition = z.enum(["fix", "wont_fix", "fast_follow"]);
export type ReviewFindingDisposition = z.infer<typeof ReviewFindingDisposition>;

export type ReviewVerdictFinding = z.infer<typeof ReviewVerdictFinding>;
const ReviewVerdictFinding = z
  .object({
    label: z.string(),
    /** `new` points at `file_comments[index]`, `prior` at the prior finding whose id is `prior_id`. */
    kind: z.enum(["new", "prior"]),
    index: z.number().int().nullable(),
    prior_id: z.string().nullable(),
    path: z.string(),
    line: z.number().int(),
    severity: z.enum(["P1", "P2", "P3"]).nullable(),
    disposition: ReviewFindingDisposition,
    reason: z.string().nullable(),
  })
  .refine((finding) =>
    finding.kind === "new"
      ? finding.index !== null && finding.index >= 0
      : finding.prior_id !== null,
  );

export interface ReviewApproval {
  /** Auto-approve is on for the PR's author, so the round is on its way to an approval decision. */
  applies: boolean;
  /** The level the approval was judged on, which can differ from the report's own `risk`. */
  risk: ReviewRisk["level"] | null;
}

export type ReviewVerdict = z.infer<typeof ReviewVerdict>;
const ReviewVerdict = z.object({
  verdict: z.enum(["approve", "changes_needed"]),
  decided_at: z.string(),
  /** Where the round's approval stands, e.g. `"approved_but_risky"`; a state newer than this build parses as a plain string (narrow with `isReviewApprovalState`), so the verdict is kept across a deploy. */
  approval_state: z
    .union([z.enum(REVIEW_APPROVAL_STATES), z.string()])
    .nullable(),
  /** The decision auto-approve recorded, e.g. `"approved"`; null while queued. */
  approval_decision: z.string().nullable(),
  approval_skip_reason: z.string().nullable(),
  /** The GitHub login of the person who acknowledged the risk of an approve held at medium or high risk; absent on reports mirrored before it was recorded. */
  risk_acknowledged_by: z.string().nullable().optional(),
  findings: z.array(ReviewVerdictFinding),
});

export interface ReviewReportMetadata {
  child_session_id: string;
  pr_url: string;
  pr_number: number;
  /** The PR head the review ran against; absent on reports mirrored before it was recorded. */
  head_sha?: string;
  /** Reviewed an unpushed snapshot; `head_sha` is then a commit only the lead's sandbox has. */
  local?: true;
  /** A push made this local review its PR's round. */
  adopted?: true;
  review_result: ReviewResult;
  wake?: {
    skipped_reason: "round_cap" | "autofix_paused";
    managed_pr_id: string | null;
    max_rounds: number | null;
  };
  /** Absent on reports mirrored before it was recorded. */
  approval?: ReviewApproval;
  /** The lead agent's verdict on this round, once it records one. */
  verdict?: ReviewVerdict;
}

function isReviewOutcome(value: unknown): value is ReviewOutcome {
  return (
    value === "approved" || value === "comment" || value === "changes_requested"
  );
}

function isReviewSeverity(value: unknown): value is ReviewSeverity {
  return value === "P1" || value === "P2" || value === "P3";
}

function isReviewFindingStatus(value: unknown): value is ReviewFindingStatus {
  return value === "fixed" || value === "still_open" || value === "invalid";
}

function isReviewRisk(value: unknown): value is ReviewRisk {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const risk = value as Partial<ReviewRisk>;
  return (
    (risk.level === "low" ||
      risk.level === "medium" ||
      risk.level === "high") &&
    Array.isArray(risk.reasons) &&
    risk.reasons.every((reason) => typeof reason === "string")
  );
}

function isReviewFileComment(value: unknown): value is ReviewFileComment {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const comment = value as Partial<ReviewFileComment>;
  return (
    typeof comment.path === "string" &&
    typeof comment.line === "number" &&
    Number.isInteger(comment.line) &&
    comment.line > 0 &&
    typeof comment.body === "string" &&
    (comment.severity === undefined || isReviewSeverity(comment.severity)) &&
    (comment.ci_covered === undefined ||
      typeof comment.ci_covered === "boolean") &&
    (comment.excerpt === undefined || typeof comment.excerpt === "string")
  );
}

function isReviewAgents(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((agent) => typeof agent === "string" && agent.length > 0)
  );
}

function isReviewPriorFinding(value: unknown): value is ReviewPriorFinding {
  if (!isReviewFileComment(value)) return false;
  const finding = value as Partial<ReviewPriorFinding>;
  return (
    isReviewSeverity(finding.severity) &&
    typeof finding.id === "string" &&
    typeof finding.head_sha === "string" &&
    isReviewFindingStatus(finding.status) &&
    typeof finding.reason === "string"
  );
}

/** Reports recorded before finding tracking have no `prior_findings`; they read as an empty list. */
function parseReviewResult(value: unknown): ReviewResult | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }
  const result = value as Partial<ReviewResult>;
  const priorFindings = result.prior_findings ?? [];
  if (
    !isReviewOutcome(result.outcome) ||
    typeof result.summary_comment !== "string" ||
    !Array.isArray(result.file_comments) ||
    !result.file_comments.every(isReviewFileComment) ||
    !Array.isArray(priorFindings) ||
    !priorFindings.every(isReviewPriorFinding)
  ) {
    return null;
  }
  const fileComments = result.file_comments.map((value) => {
    const comment = value as ReviewFileComment & { agents?: unknown };
    const { agents, ...fields } = comment;
    return {
      ...fields,
      ...(isReviewAgents(agents) ? { agents } : {}),
    };
  });
  return {
    outcome: result.outcome,
    summary_comment: result.summary_comment,
    file_comments: fileComments,
    prior_findings: priorFindings,
    ...(isReviewRisk(result.risk) ? { risk: result.risk } : {}),
  };
}

export type ReviewWake = NonNullable<ReviewReportMetadata["wake"]>;

function isReviewWake(value: unknown): value is ReviewWake {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const wake = value as Partial<ReviewWake>;
  return (
    (wake.skipped_reason === "round_cap" ||
      wake.skipped_reason === "autofix_paused") &&
    (typeof wake.managed_pr_id === "string" || wake.managed_pr_id === null) &&
    (wake.max_rounds === null ||
      (typeof wake.max_rounds === "number" &&
        Number.isInteger(wake.max_rounds) &&
        wake.max_rounds > 0))
  );
}

const NullableString = z.string().nullable();

function isReviewApproval(value: unknown): value is ReviewApproval {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const approval = value as Partial<ReviewApproval>;
  return (
    typeof approval.applies === "boolean" &&
    (approval.risk === null ||
      approval.risk === "low" ||
      approval.risk === "medium" ||
      approval.risk === "high")
  );
}

const LEGACY_DECISION_STATES = new Map<string, ReviewApprovalState>([
  ["approving", "approving"],
  ["unconfirmed", "approval_unconfirmed"],
  ["approved", "approved"],
  ["would_approve", "would_approve"],
  ["withdrawing", "withdrawn"],
  ["withdrawn", "withdrawn"],
  ["would_withdraw", "would_withdraw"],
  ["refused", "approval_refused"],
]);

const LEGACY_SKIP_REASON_STATES = new Map<string, ReviewApprovalState>([
  ["already_approved", "covered_by_earlier_approval"],
  ["proof_running", "awaiting_proof"],
  ["proof_issues", "proof_failed"],
  ["risk_not_low", "approved_but_risky"],
  ["risk_missing", "risk_not_assessed"],
]);

export function isReviewApprovalState(
  value: string,
): value is ReviewApprovalState {
  return (REVIEW_APPROVAL_STATES as readonly string[]).includes(value);
}

function legacyApprovalState(
  verdict: unknown,
  decision: string | null,
  skipReason: string | null,
): ReviewApprovalState | null {
  if (verdict === "not_yet" || verdict === "changes_needed") {
    return "changes_needed";
  }
  if (decision === null) return "approving";
  if (decision !== "skipped") {
    return LEGACY_DECISION_STATES.get(decision) ?? null;
  }
  if (skipReason === null) return null;
  return (
    LEGACY_SKIP_REASON_STATES.get(skipReason) ??
    (isReviewApprovalState(skipReason) ? skipReason : null)
  );
}

/** Mirrored reports are stored as written, so a verdict may carry `not_yet` or `verdict_not_yet` and no `approval_state`; those read as their current equivalents. */
function parseReviewVerdict(value: unknown): ReviewVerdict | null {
  const raw = z.record(z.string(), z.unknown()).safeParse(value).data;
  if (raw === undefined) return null;
  const skipReason =
    raw.approval_skip_reason === "verdict_not_yet"
      ? "changes_needed"
      : raw.approval_skip_reason;
  const decision = NullableString.safeParse(raw.approval_decision);
  const legacySkipReason = NullableString.safeParse(skipReason);
  const derivedState =
    raw.approval_state === undefined &&
    decision.success &&
    legacySkipReason.success
      ? {
          approval_state: legacyApprovalState(
            raw.verdict,
            decision.data,
            legacySkipReason.data,
          ),
        }
      : {};
  return (
    ReviewVerdict.safeParse({
      ...raw,
      verdict: raw.verdict === "not_yet" ? "changes_needed" : raw.verdict,
      approval_skip_reason: skipReason,
      ...derivedState,
    }).data ?? null
  );
}

/** Accepts anything shaped like the mirrored `review_report` metadata; null when a field is missing or malformed. A malformed `verdict` or `approval` is dropped. */
export function parseReviewReportMetadata(
  raw: object,
): ReviewReportMetadata | null {
  const report = raw as Partial<ReviewReportMetadata>;
  const reviewResult = parseReviewResult(report.review_result);
  if (
    typeof report.child_session_id !== "string" ||
    typeof report.pr_url !== "string" ||
    typeof report.pr_number !== "number" ||
    !Number.isInteger(report.pr_number) ||
    reviewResult === null ||
    (report.head_sha !== undefined && typeof report.head_sha !== "string") ||
    (report.wake !== undefined && !isReviewWake(report.wake))
  ) {
    return null;
  }
  const verdict = parseReviewVerdict(report.verdict);
  return {
    child_session_id: report.child_session_id,
    pr_url: report.pr_url,
    pr_number: report.pr_number,
    ...(report.head_sha ? { head_sha: report.head_sha } : {}),
    ...(report.local === true ? { local: true as const } : {}),
    ...(report.adopted === true ? { adopted: true as const } : {}),
    review_result: reviewResult,
    ...(report.wake ? { wake: report.wake } : {}),
    ...(isReviewApproval(report.approval) ? { approval: report.approval } : {}),
    ...(verdict ? { verdict } : {}),
  };
}

// One parse per raw object, so a report keeps its identity across renders
// and index rebuilds can tell "unchanged" from "changed" by reference.
const parsedByRaw = new WeakMap<object, ReviewReportMetadata | null>();

export function reviewReportMetadata(message: {
  role: string;
  metadata?: Record<string, unknown> | null;
}): ReviewReportMetadata | null {
  if (message.role !== "agent") return null;
  const raw = message.metadata?.review_report;
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return null;
  }
  let parsed = parsedByRaw.get(raw);
  if (parsed === undefined) {
    parsed = parseReviewReportMetadata(raw);
    parsedByRaw.set(raw, parsed);
  }
  return parsed;
}

export function isReviewReportMessage(message: {
  role: string;
  metadata?: Record<string, unknown> | null;
}): boolean {
  return reviewReportMetadata(message) !== null;
}
